import { Modal, Row, Col, Alert, Button, Container, Form } from "react-bootstrap";
import PortalService from '../../../services/portal.service';
import { useContext, useEffect, useMemo, useState } from "react";
import StateContext from '../../../context/State';
import { VerifiedClient, ClientPortalModalType, CaseClient, ClientMatchMap } from '../../../types';
import React from 'react';
import { useAppSelector } from '../../../../../redux/hooks';
import { ReducerEnum, lddStateObject, setClientPortalModalType } from '../../../../../redux/reducer';
import IDV from './IDV';
import { runInnerFunction } from "../../../../../compatManager";
import { getDataReader, RPWDataReader } from "../../../../../livedataFuncs";
import { renderClientIDDetailsInNewWindow } from './IDVPrintTemplate';
import { renderDownloadPDF, renderIDVResultPDF } from "./IDVDownloadTemplate";
import { buildClientMatchMap, dedupIDVBySession, evaluateIDVResult, findClientMatch, isPreReconfigSubmission, idvAlreadyImported, isLegacyImportableIDV } from "./IDVUtils";


export type IdentityVerificationTaskModalProps = {
    dispatch: (arg0: {
        type: ReducerEnum;
        payload: any;
        key?: string;
        extraPayload?: Record<string, string>;
    }) => void;
}

const IdentityVerificationTaskModal = (props: IdentityVerificationTaskModalProps) => {

    const { state } = useContext(StateContext);
    const lawyerService = new PortalService(state);
    const province = lawyerService.getProvince()

    const [verificationData, setVerificationData] = useState<VerifiedClient[]>([]);
    const show: boolean = useAppSelector((state) => state.ClientPortalState?.ModalType === ClientPortalModalType.ID_VERIFICATION);
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const processor: RPWDataReader = getDataReader(livedataRootContainer?.dataRoot);
    // State for Send Email modal (handles both bulk reminder and per-record re-request flows)
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailMode, setEmailMode] = useState<'reminder' | 'reRequest'>('reminder');
    const [selectedClientIndexes, setSelectedClientIndexes] = useState<number[]>([]);
    const [reminderNote, setReminderNote] = useState<string>("");
    // Case clients (name + email), storing clients' name and email, used for sendIDV email modal
    const caseClients: CaseClient[] = (processor?.getClientEmail?.() || []) as CaseClient[];
    const hasCaseClients = caseClients.length > 0;

    // Build a match map from case clients for auto-matching IDV records to clients
    const clientMatchMap: ClientMatchMap = useMemo(
        () => buildClientMatchMap(caseClients),
        [caseClients]
    );

    // eslint-disable-next-line
    useEffect(() => { getIdentityVerificationData(); }, [show])
    const getIdentityVerificationData = async () => {
        try {
            if (!show) return;
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: JSON.stringify({
                    "Appid": state.appId,
                    "Product": lawyerService.getSessionAppCode()
                })
            };
            fetch(`/${process.env.REACT_APP_MOBILE_ENDPOINT}/api/getTableRecords?SessionID=${lawyerService.getSessionId()}&compat=1`, requestOptions)
                .then(response => response.text())
                .then(result => {
                    setVerificationData(dedupIDVBySession(parseGetTableRecords(result)));
                })
                .catch(error => console.log('error', error));
        } catch (err) {
            console.log(err);
        }
    };

    const parseGetTableRecords = (result: string): VerifiedClient[] => {
        if (result.includes('Detailed Error - 404.0 - Not Found')) { return [] }

        const xmlData = result;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(JSON.parse(xmlData).result, "text/xml");
        const EntityList = xmlDoc.getElementsByTagName('Entity');
        const data: VerifiedClient[] = [];
        for (let i = 0; i < EntityList.length; i++) {
            data.push(parseTableRecord(EntityList[i]));
        }
        return data;
    };

    const parseTableRecord = (Entity: Element): VerifiedClient => {
        const clientInformation: VerifiedClient["clientInformation"] = {
            Name: "",
            Status: "",
            Timestamp: "",
            Type: "",
            id: "",
        };
        mappingAttribute(clientInformation, Entity);
        const clientIDImages: VerifiedClient["clientIDImages"] = {
            backImage: "",
            frontImage: "",
            verifyFaceFrame: "",
        };
        mappingAttribute(clientIDImages, Entity.childNodes[0] as Element);
        const clientIdFraudCheck: VerifiedClient["clientIdFraudCheck"] = {
            failedChecks: "",
            json: "",
            notPerformedChecks: "",
            passedChecks: "",
            score: "",
            totalChecks: "",
            type: "",
            certainty: "",
        };
        mappingAttribute(clientIdFraudCheck, Entity.childNodes[0].childNodes[0] as Element);
        
        if (Entity.hasAttribute("expiredCheck")) {
            clientIdFraudCheck.expiredCheck = Entity.getAttribute("expiredCheck") as 'PASS' | 'FAIL';
        }
        if (Entity.hasAttribute("screenCheck")) {
            clientIdFraudCheck.screenCheck = Entity.getAttribute("screenCheck") as 'PASS' | 'FAIL';
        }
        if (Entity.hasAttribute("photocopyCheck")) {
            clientIdFraudCheck.photocopyCheck = Entity.getAttribute("photocopyCheck") as 'PASS' | 'FAIL';
        }
        
        var fraudCheckJson = JSON.parse(clientIdFraudCheck.json);
        const FaceVerificationPassed = fraudCheckJson.FaceVerificationPassed;
        const skipFaceVerification = fraudCheckJson.skipFaceVerification;
        return { Entity: Entity.outerHTML, clientInformation, clientIDImages, clientIdFraudCheck, FaceVerificationPassed, skipFaceVerification};    
        function mappingAttribute(copyTo: VerifiedClient["clientIDImages" | "clientInformation" | "clientIdFraudCheck"], node: Element) {
            if (node && node.attributes) {
                Object.keys(copyTo).forEach((attributeName) => {
                    if (node.hasAttribute(attributeName)) {
                        copyTo[attributeName] = node.getAttribute(attributeName);
                    }
                })
            }
        }
    };

    let setPageDirtyOnClose = false;
    const setDirty = () => { setPageDirtyOnClose = true; }
    const CloseHandler = () => {
        props.dispatch(setClientPortalModalType(ClientPortalModalType.NONE));
        if (setPageDirtyOnClose) {
            runInnerFunction("SetPageDirty", null);
        }
    }

    const getFileNumber = () => {
        return processor.getFileNumber()
    }

    // Handler for the bulk "Send Reminder Email" header button — defaults to all clients selected.
    const handleSendReminderEmail = () => {
        if (!hasCaseClients) return;
        setEmailMode('reminder');
        setSelectedClientIndexes(caseClients.map((_, idx) => idx));
        setReminderNote("");
        setShowEmailModal(true);
    };

    // Handler for the per-record "Request Re-Verification" button — defaults to the matched client only.
    const handleRequestReVerification = (matchedIndex: number | undefined) => {
        if (!hasCaseClients) return;
        setEmailMode('reRequest');
        setSelectedClientIndexes(matchedIndex !== undefined ? [matchedIndex] : []);
        setReminderNote("");
        setShowEmailModal(true);
    };

    const handleCloseEmailModal = () => {
        setShowEmailModal(false);
        setReminderNote("");
        setSelectedClientIndexes([]);
    };

    const toggleClientSelection = (index: number) => {
        setSelectedClientIndexes(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const handleConfirmSendEmail = async () => {
        try {
            const selectedClients = selectedClientIndexes.map(i => caseClients[i]);
            if (selectedClients.length === 0) {
                // Already disabled in UI when no clients are selected
                alert("Please select at least one client.");
                return;
            }
            const payload = {
                clients: selectedClients.map(c => ({ name: c.name, email: c.email })),
                noteText: reminderNote || "",
            };

            const response = emailMode === 'reRequest'
                ? await lawyerService.sendIDVReRequest(state.portalId, payload)
                : await lawyerService.sendIDVReminder(state.portalId, payload);

            if (response?.message === "error") {
                alert(emailMode === 'reRequest' ? "Failed to send re-request email." : "Failed to send reminder email.");
                return;
            }

            alert(emailMode === 'reRequest' ? "Re-request email(s) sent." : "Reminder email(s) sent.");
            handleCloseEmailModal();
        } catch (e) {
            console.error("Failed to send IDV email:", e);
            alert(emailMode === 'reRequest' ? "Failed to send re-request email." : "Failed to send reminder email.");
        }
    };

    const modalHeaderText = emailMode === 'reRequest' ? 'Send Re-Request Email' : 'Send Reminder Email';
    const submitButtonText = emailMode === 'reRequest' ? 'Send Re-Request Email' : 'Send Reminder Email';
    const reminderListLabel = emailMode === 'reRequest' ? 'Send ID Verification Re-Request to:' : 'Send ID Verification Reminder to:';

    return (
    <>
        <style>{`
            .idv-modal-wide { max-width: 1500px; }
            .idv-modal-wide .modal-content {
                max-height: 70vh;
                background-color: var(--bg-30);
            }
        `}</style>
        {show &&
            <Modal
                show={show}
                onHide={CloseHandler}
                dialogClassName="idv-modal-wide"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                scrollable
            >
                <Modal.Header closeButton>
                    <Container fluid className="gx-0">
                        <Row className="gx-0">
                            <Col>
                                <Modal.Title id="contained-modal-title-vcenter">
                                    Submitted Verifications
                                </Modal.Title>
                            </Col>
                            <Col className="align-right">
                                {/* Header Download/Print are kept only for legacy (pre-reconfig)
                                    records — the new per-record cards have their own buttons,
                                    and showing the bulk ones alongside would be redundant. */}
                                {verificationData.some(isPreReconfigSubmission) && (
                                    <>
                                        <Button variant="outline-primary" title="Download Details" onClick={() => renderDownloadPDF(verificationData.filter(isPreReconfigSubmission), province, getFileNumber())} style={{ marginRight: '5px' }} disabled={verificationData.length === 0}>
                                            <i className="fa-regular fa-download"></i> Download
                                        </Button>
                                        <Button title="Print Details" onClick={() => renderClientIDDetailsInNewWindow(verificationData.filter(isPreReconfigSubmission), province, getFileNumber())} style={{ marginRight: '15px' }} disabled={verificationData.length === 0}>
                                            <i className="fa-regular fa-print"></i> Print
                                        </Button>
                                    </>
                                )}

                                <Button
                                    title="Send Reminder Email"
                                    onClick={handleSendReminderEmail}
                                    disabled={!hasCaseClients}
                                    className="btn btn-primary"
                                >
                                    <i className="fa-regular fa-envelope"></i> Send Reminder Email
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Header>

                <Modal.Body>
                    {
                        verificationData.length !== 0 ?
                            <>
                                {['AB','ON','NS','BC'].includes(province) && verificationData.every(client => client.skipFaceVerification) &&
                                    <Alert variant="warning">
                                        Client Face Verification has been Disabled for this File.
                                    </Alert>
                                }
                                {(() => {
                                    // Resolve each IDV's matched case client up-front so we can dedupe
                                    // the actual import. Every matched record still displays as imported;
                                    // only the first record per client writes the Verification entry, so
                                    // we never append duplicates for a single client.
                                    const usedClients = new Set<CaseClient>();
                                    return verificationData.map((verifiedClient: VerifiedClient, index) => {
                                    // Single client: auto-link the first IDV to that client
                                    // Multiple clients: use name matching (skip if duplicate key)
                                    const candidate = caseClients.length === 1
                                        ? caseClients[0]
                                        : (() => {
                                            const match = findClientMatch(verifiedClient.clientInformation.Name, clientMatchMap);
                                            return match && !match.isDuplicate ? match.client : undefined;
                                        })();
                                    // Reasons this record must not run the write, evaluated up front:
                                    //  - alreadyStored: this exact ID is already on the client in the
                                    //    file (e.g. a prior session). The legacy importIdentification
                                    //    has no dedupe and would append a duplicate.
                                    //  - !importable: the document type isn't in the legacy writer's
                                    //    supported set. The legacy returns an <Identification> without
                                    //    a <Verification> child for those, and importIDVRetry's
                                    //    insertBefore then crashes on a null first argument.
                                    //  - !passed: only a passing verification writes into the file.
                                    //    Pre-reconfig records have no Pass/Fail outcome, so they keep
                                    //    their previous behavior rather than being gated on one.
                                    const alreadyStored = !!candidate?.nameFull &&
                                        idvAlreadyImported(verifiedClient, processor?.getClientExistingIds?.(candidate.nameFull) || []);
                                    const importable = isLegacyImportableIDV(verifiedClient);
                                    const passed = isPreReconfigSubmission(verifiedClient)
                                        || evaluateIDVResult(verifiedClient).outcome === 'Pass';

                                    // Only one record per client runs the real import. A record claims
                                    // its client's slot ONLY if it will actually write, so a blocked
                                    // record doesn't stop a later passing scan of the same client from
                                    // importing. `importedElsewhere` means the ID is in the file but
                                    // written by something other than this record — the one case where
                                    // the confirmation should show without a write of our own.
                                    let autoImport = false;
                                    let importedElsewhere = false;
                                    if (candidate) {
                                        if (usedClients.has(candidate)) {
                                            importedElsewhere = true;
                                        } else if (alreadyStored) {
                                            importedElsewhere = true;
                                        } else if (importable && passed) {
                                            autoImport = true;
                                            usedClients.add(candidate);
                                        }
                                    }
                                    const matchedIndex = candidate ? caseClients.findIndex(c => c.email === candidate.email && c.name === candidate.name) : -1;
                                    return (
                                        <IDV
                                            idv={verifiedClient}
                                            province={province}
                                            setDirty={setDirty}
                                            matchedClient={candidate}
                                            autoImport={autoImport}
                                            importedElsewhere={importedElsewhere}
                                            onDownload={() => {
                                                if (isPreReconfigSubmission(verifiedClient)) {
                                                    renderDownloadPDF([verifiedClient], province, getFileNumber());
                                                } else {
                                                    renderIDVResultPDF(verifiedClient, {
                                                        fileNumber: getFileNumber(),
                                                        sourceFirm: processor?.getFirmName?.() || '',
                                                    });
                                                }
                                            }}
                                            onPrint={() => {
                                                if (isPreReconfigSubmission(verifiedClient)) {
                                                    renderClientIDDetailsInNewWindow([verifiedClient], province, getFileNumber());
                                                } else {
                                                    renderIDVResultPDF(verifiedClient, {
                                                        fileNumber: getFileNumber(),
                                                        sourceFirm: processor?.getFirmName?.() || '',
                                                        mode: 'print',
                                                    });
                                                }
                                            }}
                                            onRequestReVerification={() => handleRequestReVerification(matchedIndex >= 0 ? matchedIndex : undefined)}
                                            key={verifiedClient.clientInformation.Name + "-fragment-" + index}
                                        />
                                    )
                                    });
                                })()}
                            </>
                            :
                            <Alert>No Clients have used the ClientConnect App to identify themselves.</Alert>
                    }
                </Modal.Body>
            </Modal>
        }

        {/* Send IDV Email modal — handles both bulk reminders and per-record re-requests */}
        <Modal
            show={showEmailModal}
            onHide={handleCloseEmailModal}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{modalHeaderText}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* If there is more than one client, show selection list */}
                {caseClients.length > 1 && (
                    <>
                        <div className="mb-3">
                            <strong>{reminderListLabel}</strong>
                        </div>

                        <Form>
                            {caseClients.map((client, index) => (
                                <Form.Check
                                    key={client.email + index}
                                    type="checkbox"
                                    className="mb-3"
                                    checked={selectedClientIndexes.includes(index)}
                                    onChange={() => toggleClientSelection(index)}
                                    label={
                                        <div>
                                            <div>{client.name}</div>
                                            <div
                                                className="text-muted"
                                                style={{ fontSize: "0.9rem" }}
                                            >
                                                {client.email}
                                            </div>
                                        </div>
                                    }
                                />
                            ))}
                        </Form>
                    </>
                )}

                {/* Always show note area */}
                <Form.Group className={caseClients.length > 1 ? "mt-3" : ""}>
                    <Form.Label>Add Note to Request Email</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Enter Note Text"
                        value={reminderNote}
                        onChange={(e) => setReminderNote(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="link" onClick={handleCloseEmailModal}>
                    Cancel
                </Button>
                <Button onClick={handleConfirmSendEmail} disabled={ caseClients.length > 1 && selectedClientIndexes.length === 0 }>
                    {submitButtonText}
                </Button>
            </Modal.Footer>
        </Modal>
    </>
    );
}

export default IdentityVerificationTaskModal;
